import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { isObject } from 'helpers/verify';

/*
  "LocalizableMessage" is a uniform interface for rendering messages.
  In the common case, developers should define the message using `defineMessages`
  from react-intl and pass that message object to LocalizableMessage.  When the
  origin of the message is unknown, this module will ensure that the message
  will be rendered in the safest, most appropriate manner.
  Use of `tagName` is provided for backwards compatability and is discouraged.

  //Preferred usage:
  const messages = defineMessages({
    hello: {
      id: 'example.id',
      defaultMessage: 'Hello <a href="{LINK}">world</a>!',
      hasHtml: true,
      values: { LINK: 'http://example.com' },
    },
  });
  <LocalizableMessage message={messages.hello} />

  // With a render prop
  <LocalizableMessage message={messages.hello}>
    {msg => (<div>{msg}</div>)}
  </LocalizableMessage>

  // All of the following forms are supported when the origin of the message is ambiguous:

  // String:
  <LocalizableMessage message="Hello world!" />

  // String with html:
  <LocalizableMessage
    hasHtml
    message="Hello <a href="http://example.com">world</a>!"
  />

  // Plain object:
  <LocalizableMessage message={{
    id: 'example.id',
    defaultMessage: 'Hello world!',
  }} />

  // Plain object with html:
  <LocalizableMessage message={{
    id: 'example.id',
    defaultMessage: 'Hello <a href="{LINK}">world</a>!',
    hasHtml: true,
    values: { LINK: 'http://example.com' },
  }} />

  Extended documentation on usage can be found in the i18n docs
  (https://docs.classpass.engineering/guilds/web/i18n/#displaying-conditional-inline-strings)
*/

export const LocalizableMessage = ({ hasHtml, message, ...props }) => {
  const tagName = props.tagName || 'span';

  if (isObject(message)) {
    return hasHtml || message.hasHtml ? (
      <FormattedHTMLMessage tagName={tagName} {...props} {...message} />
    ) : (
      <FormattedMessage tagName={tagName} {...props} {...message} />
    );
  }

  if (props.children) {
    return props.children(message);
  }

  return hasHtml
    ? React.createElement(tagName, { dangerouslySetInnerHTML: { __html: message } })
    : React.createElement(tagName, {}, message);
};

export const LocalizationShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string,
  hasHtml: PropTypes.bool,
  values: PropTypes.object,
});

export const LocalizableMessageShape = PropTypes.oneOfType([LocalizationShape, PropTypes.string]);

LocalizableMessage.propTypes = {
  children: PropTypes.func,
  message: LocalizableMessageShape.isRequired,
  tagName: PropTypes.string,
  hasHtml: PropTypes.bool,
};
